import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, MessageCircle, FileText, Calendar, CheckCircle, Users } from 'lucide-react';

const ConsultationPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  };

  const consultationSteps = [
    {
      number: '01',
      icon: MessageCircle,
      title: 'Initial Enquiry',
      description: 'Contact us via the form or email. Share your areas of interest and any questions you have. Our team will respond within 48 hours to arrange your consultation.',
      duration: '1-2 days'
    },
    {
      number: '02',
      icon: Calendar,
      title: 'Consultation Appointment',
      description: 'Meet with Mr Maheshwari to discuss your goals in detail. This is an opportunity to ask questions, understand your options, and receive an honest assessment.',
      duration: '45-60 mins'
    },
    {
      number: '03',
      icon: FileText,
      title: 'Treatment Plan',
      description: 'If you decide to proceed, you\'ll receive a detailed treatment plan outlining the procedure, preparation, expected outcomes, and recovery timeline.',
      duration: '1-2 weeks'
    },
    {
      number: '04',
      icon: CheckCircle,
      title: 'Pre-Operative Assessment',
      description: 'A thorough health assessment ensures you\'re fit for surgery. This may include blood tests and any specialist consultations if required.',
      duration: '1-2 weeks'
    },
    {
      number: '05',
      icon: Users,
      title: 'Surgery & Aftercare',
      description: 'Your procedure is performed in an accredited facility. Comprehensive aftercare includes follow-up appointments and support throughout your recovery.',
      duration: 'Ongoing'
    }
  ];

  const faqs = [
    {
      question: 'How much does a consultation cost?',
      answer: 'Consultation fees vary depending on the complexity of the procedure discussed. You will be advised of the fee when booking. This fee is often deductible from your treatment cost if you proceed.'
    },
    {
      question: 'Do I need a GP referral?',
      answer: 'A GP referral is not required for private consultations. However, if you have a medical condition that may affect your treatment, sharing relevant medical records is helpful.'
    },
    {
      question: 'What should I bring to my consultation?',
      answer: 'Please bring any relevant medical records, a list of current medications, and photographs if relevant. Most importantly, bring your questions—we\'re here to answer them.'
    },
    {
      question: 'Is there any pressure to commit during the consultation?',
      answer: 'Absolutely not. The consultation is designed to inform and empower you. There is no obligation to proceed, and we encourage patients to take time to consider their options.'
    },
    {
      question: 'How long until I can have surgery after the consultation?',
      answer: 'This depends on the procedure and your individual circumstances. Some procedures may be scheduled within weeks, while others require additional preparation time. A mandatory cooling-off period applies to cosmetic procedures.'
    }
  ];

  return (
    <div className="bg-clinic-bg" data-testid="consultation-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28" data-testid="consultation-hero">
        <div className="container-clinic">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial="initial"
              animate="animate"
              variants={{
                animate: { transition: { staggerChildren: 0.1 } }
              }}
            >
              <motion.p variants={fadeInUp} className="text-caption mb-4">
                Consultation
              </motion.p>
              <motion.h1 variants={fadeInUp} className="text-heading-1 text-clinic-charcoal mb-8">
                Your journey begins with a conversation
              </motion.h1>
              <motion.p variants={fadeInUp} className="text-body-lg mb-8">
                A consultation is a two-way dialogue. It's an opportunity for you to 
                share your goals and concerns, and for Mr Maheshwari to provide honest, 
                expert guidance. There's no pressure, no obligation—just clarity.
              </motion.p>
              <motion.div variants={fadeInUp}>
                <Link
                  to="/contact"
                  className="btn-primary"
                  data-testid="consultation-hero-cta"
                >
                  Request a Consultation
                </Link>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://customer-assets-wrfwihn1.emergentagent.net/job_0b41d704-a68a-4dc5-aa3c-437daaaec2be/artifacts/6a7fe72b20eb9e01_DSC02034-26.webp"
                  alt="Calm clinical environment"
                  className="w-full h-full object-cover"
                  data-testid="consultation-hero-image"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="section-padding bg-white" data-testid="what-to-expect-section">
        <div className="container-clinic">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mb-16"
          >
            <p className="text-caption mb-4">What to Expect</p>
            <h2 className="text-heading-2 text-clinic-charcoal mb-6">
              During your consultation
            </h2>
            <p className="text-body-lg">
              Your consultation with Mr Maheshwari typically lasts 45-60 minutes. 
              This is your time to discuss your concerns openly and receive considered, 
              expert advice.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Clock,
                title: 'Dedicated Time',
                description: 'Unhurried appointments ensuring all your questions are addressed thoroughly.'
              },
              {
                icon: MessageCircle,
                title: 'Open Dialogue',
                description: 'An honest discussion about your goals, expectations, and any concerns.'
              },
              {
                icon: FileText,
                title: 'Clear Information',
                description: 'Detailed explanation of options, procedures, risks, and realistic outcomes.'
              }
            ].map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="p-8 bg-clinic-bg border border-clinic-border"
                data-testid={`expect-card-${index}`}
              >
                <div className="w-12 h-12 flex items-center justify-center border border-clinic-border mb-6">
                  <item.icon size={20} className="text-clinic-charcoal" />
                </div>
                <h3 className="text-heading-3 text-clinic-charcoal mb-3">{item.title}</h3>
                <p className="text-body-lg">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="section-padding" data-testid="process-timeline-section">
        <div className="container-clinic">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <p className="text-caption mb-4">The Process</p>
            <h2 className="text-heading-2 text-clinic-charcoal">
              From enquiry to aftercare
            </h2>
          </motion.div>

          <div className="space-y-6">
            {consultationSteps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="grid grid-cols-1 md:grid-cols-12 gap-6 p-8 bg-white border border-clinic-border"
                data-testid={`process-step-${index}`}
              >
                <div className="md:col-span-1">
                  <span className="text-4xl font-heading text-clinic-border">{step.number}</span>
                </div>
                <div className="md:col-span-8">
                  <div className="flex items-center gap-3 mb-3">
                    <step.icon size={20} className="text-clinic-charcoal" />
                    <h3 className="text-heading-3 text-clinic-charcoal">{step.title}</h3>
                  </div>
                  <p className="text-body-lg">{step.description}</p>
                </div>
                <div className="md:col-span-3 flex items-center justify-start md:justify-end">
                  <span className="text-caption">{step.duration}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="section-padding bg-clinic-muted" data-testid="faqs-section">
        <div className="container-clinic">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <p className="text-caption mb-4">FAQs</p>
            <h2 className="text-heading-2 text-clinic-charcoal">
              Common questions
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={faq.question}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="p-8 bg-white border border-clinic-border"
                data-testid={`faq-item-${index}`}
              >
                <h3 className="font-heading text-xl text-clinic-charcoal mb-4">{faq.question}</h3>
                <p className="text-body-lg">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding" data-testid="consultation-cta-section">
        <div className="container-clinic">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl mx-auto text-center"
          >
            <h2 className="text-heading-2 text-clinic-charcoal mb-6">
              Ready to take the first step?
            </h2>
            <p className="text-body-lg mb-10">
              Request a consultation to discuss your options with Mr Maheshwari. 
              We aim to respond to all enquiries within 48 hours.
            </p>
            <Link
              to="/contact"
              className="btn-primary"
              data-testid="consultation-cta-btn"
            >
              Request a Consultation
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ConsultationPage;
